import React, { useState, useEffect } from 'react';
import {
    Settings, ChevronDown, Save, RefreshCw, Cpu, Sparkles, Zap, Power,
    Database, Search, Building2, ListFilter, ShieldCheck, Globe2, FileText, Tags
} from 'lucide-react';
import { toast } from 'sonner';
import {
    AVAILABLE_MODELS,
    LOCAL_FLAN_T5_MODEL,
    GEMINI_EXTRACTION_MODELS,
    EXTRACTION_ENGINES,
    DEFAULT_SPEC_EXTRACT_MODEL,
    DEFAULT_CLASSIFICATION_MODEL,
    DEFAULT_EXTRACTION_ENGINE,
    DEFAULT_GEMINI_EXTRACTION_MODEL,
    DEFAULT_CONDITION_GENERATION_MODEL,
    DEFAULT_AGENT_SETTINGS,
    PIPELINE_STEPS,
    DEFAULT_PIPELINE_STEPS,
    DEFAULT_SEARCH_WITH_DESCRIPTION,
    normalizeGeminiModel,
} from '../lib/aiSettingsUtils';

/** The same model picker for every AI step */
function ModelPicker({ number, icon: Icon, title, description, options, value, onSelect, className = '' }) {
    return (
        <div className={`rounded-2xl border border-slate-200 bg-white/75 p-5 mb-1 shadow-sm ${className}`}>
            <label className="flex items-center gap-1.5 text-sm font-bold text-slate-600 mb-1">
                {number && <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">{number}</span>}
                {Icon && <Icon className="w-4 h-4 text-cyan-600" />}
                {title}
            </label>
            {description && <p className="text-xs text-slate-500 mb-3 ml-9">{description}</p>}
            <div className="space-y-2.5">
                {options.map(model => {
                    const isActive = value === model.id;
                    return (
                        <button
                            key={model.id}
                            type="button"
                            onClick={() => onSelect(model.id)}
                            aria-pressed={isActive}
                            className="w-full px-4 py-3 rounded-xl border text-left transition-all duration-200 flex items-center justify-between gap-3"
                            style={{
                                background: isActive ? '#eff6ff' : '#ffffff',
                                borderColor: isActive ? '#0f6cbd' : '#e2e8f0',
                                boxShadow: isActive ? '0 0 0 2px rgba(15,108,189,0.15)' : 'none',
                            }}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <span className={`w-2.5 h-2.5 shrink-0 rounded-full transition-colors ${isActive ? 'bg-primary-500' : 'bg-slate-300'}`}></span>
                                <div className="min-w-0">
                                    <span className={`text-sm font-medium ${isActive ? 'text-slate-800' : 'text-slate-600'}`}>{model.label}</span>
                                    <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{model.provider}</span>
                                    {model.desc && <p className="text-[11px] text-slate-400 mt-0.5">{model.desc}</p>}
                                </div>
                            </div>
                            {isActive && (
                                <span className="text-xs px-2 py-0.5 rounded-lg font-bold text-cyan-700 shrink-0" style={{
                                    background: 'var(--neu-bg)',
                                    boxShadow: 'inset 1px 1px 3px rgba(163,177,198,0.3), inset -1px -1px 3px rgba(255,255,255,0.5)',
                                }}>Selected</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

const STEP6_MODEL_OPTIONS = [...AVAILABLE_MODELS, LOCAL_FLAN_T5_MODEL];
const modelLabel = (id) => STEP6_MODEL_OPTIONS.find(m => m.id === id)?.label || id;

function SettingsDropdown() {
    const [isOpen, setIsOpen] = useState(false);

    const [specExtractModel, setSpecExtractModel] = useState(DEFAULT_SPEC_EXTRACT_MODEL);
    const [classificationModel, setClassificationModel] = useState(DEFAULT_CLASSIFICATION_MODEL);
    const [extractionEngine, setExtractionEngine] = useState(DEFAULT_EXTRACTION_ENGINE);
    const [geminiExtractionModel, setGeminiExtractionModel] = useState(DEFAULT_GEMINI_EXTRACTION_MODEL);
    const [conditionGenerationModel, setConditionGenerationModel] = useState(DEFAULT_CONDITION_GENERATION_MODEL);
    const [pipelineSteps, setPipelineSteps] = useState({ ...DEFAULT_PIPELINE_STEPS });
    const [searchWithDescription, setSearchWithDescription] = useState(DEFAULT_SEARCH_WITH_DESCRIPTION);
    const [agent, setAgent] = useState({ ...DEFAULT_AGENT_SETTINGS });
    const [activeSettingsTab, setActiveSettingsTab] = useState('pipeline');
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem('aiSettings');
        if (!saved) return;

        try {
            const parsed = JSON.parse(saved);
            setSpecExtractModel(normalizeGeminiModel(parsed.specExtractModel, DEFAULT_SPEC_EXTRACT_MODEL));
            setClassificationModel(normalizeGeminiModel(parsed.classificationModel, DEFAULT_CLASSIFICATION_MODEL));
            setExtractionEngine(parsed.extractionEngine || DEFAULT_EXTRACTION_ENGINE);
            setGeminiExtractionModel(normalizeGeminiModel(parsed.geminiExtractionModel, DEFAULT_GEMINI_EXTRACTION_MODEL));
            setConditionGenerationModel(normalizeGeminiModel(parsed.conditionGenerationModel, DEFAULT_CONDITION_GENERATION_MODEL));
            if (parsed.pipelineSteps) {
                setPipelineSteps({ ...DEFAULT_PIPELINE_STEPS, ...parsed.pipelineSteps });
            }
            setAgent({ ...DEFAULT_AGENT_SETTINGS, ...(parsed.agent || {}) });
            if (typeof parsed.searchWithDescription === 'boolean') setSearchWithDescription(parsed.searchWithDescription);
        } catch (e) {
            console.error('Failed to load settings:', e);
        }
    }, []);

    const handleSave = () => {
        try {
            localStorage.setItem('aiSettings', JSON.stringify({
                specExtractModel,
                classificationModel,
                extractionEngine,
                geminiExtractionModel,
                conditionGenerationModel,
                pipelineSteps,
                searchWithDescription,
                agent,
            }));
            setHasChanges(false);
            window.dispatchEvent(new CustomEvent('aiSettingsChanged', {
                detail: { extractionEngine, specExtractModel, classificationModel, geminiExtractionModel, conditionGenerationModel, agent }
            }));
            const engineLabel = extractionEngine === 'flan-t5'
                ? 'Flan-T5'
                : extractionEngine === 'openai-luna'
                    ? 'GPT-5.6 Luna'
                    : 'Gemini+Memory';
            const modelLabel = extractionEngine === 'gemini-fewshot'
                ? ` (${GEMINI_EXTRACTION_MODELS.find(m => m.id === geminiExtractionModel)?.label || geminiExtractionModel})`
                : '';
            toast.success(`Settings saved — ${engineLabel}${modelLabel}`);
        } catch (e) {
            console.error('Failed to save settings:', e);
            toast.error('Failed to save settings');
        }
    };

    const handleReset = () => {
        setSpecExtractModel(DEFAULT_SPEC_EXTRACT_MODEL);
        setClassificationModel(DEFAULT_CLASSIFICATION_MODEL);
        setExtractionEngine(DEFAULT_EXTRACTION_ENGINE);
        setGeminiExtractionModel(DEFAULT_GEMINI_EXTRACTION_MODEL);
        setConditionGenerationModel(DEFAULT_CONDITION_GENERATION_MODEL);
        setPipelineSteps({ ...DEFAULT_PIPELINE_STEPS });
        setSearchWithDescription(DEFAULT_SEARCH_WITH_DESCRIPTION);
        setAgent({ ...DEFAULT_AGENT_SETTINGS });
        setHasChanges(true);
        toast.info('Settings reset to defaults');
    };

    // Neumorphic style helpers
    const neuInset = {
        background: 'var(--neu-bg)',
        boxShadow: 'inset 3px 3px 7px rgba(163,177,198,0.5), inset -3px -3px 7px rgba(255,255,255,0.7)',
    };

    const neuRaised = {
        background: 'var(--neu-bg)',
        boxShadow: '3px 3px 8px 0 rgba(163,177,198,0.4), -3px -3px 8px 0 rgba(255,255,255,0.6)',
    };

    const neuFloat = {
        background: 'var(--neu-bg)',
        boxShadow: '10px 10px 20px 0 rgba(163,177,198,0.65), -10px -10px 20px 0 rgba(255,255,255,0.85), 0 4px 24px rgba(0,0,0,0.05)',
        animation: 'neuPop 0.25s cubic-bezier(0.34,1.56,0.64,1)',
    };

    const activeEngine = EXTRACTION_ENGINES.find(e => e.id === extractionEngine);
    // Step 6 shows one model list; the choice maps onto the extraction engine + Gemini model
    const step6Model = extractionEngine === 'flan-t5' ? 'flan-t5'
        : extractionEngine === 'openai-luna' ? 'gpt-5.6-luna'
            : geminiExtractionModel;
    const selectStep6Model = (id) => {
        if (id === 'flan-t5') setExtractionEngine('flan-t5');
        else if (id === 'gpt-5.6-luna') setExtractionEngine('openai-luna');
        else { setExtractionEngine('gemini-fewshot'); setGeminiExtractionModel(id); }
        setHasChanges(true);
    };
    const pipelineIcons = {
        step0: Database, step1: Search, step1_5: Building2,
        step2: ListFilter, step2_5: ShieldCheck, step3: Globe2, step3_5: Sparkles,
        step4: FileText, step4_5: Tags, step6: Zap, step6_5: Sparkles,
    };
    const engineIcons = {
        'flan-t5': Cpu,
        'gemini-fewshot': Sparkles,
        'openai-luna': Zap,
    };
    const ActiveEngineIcon = engineIcons[extractionEngine] || Cpu;

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl transition-all duration-200 relative text-sm font-semibold"
                style={{
                    background: isOpen
                        ? '#0f6cbd'
                        : 'var(--neu-bg)',
                    color: isOpen ? '#fff' : '#475569',
                    boxShadow: isOpen
                        ? 'inset 3px 3px 7px rgba(0,0,0,0.15), inset -3px -3px 7px rgba(255,255,255,0.1)'
                        : '4px 4px 10px 0 rgba(163,177,198,0.5), -4px -4px 10px 0 rgba(255,255,255,0.7)',
                }}
            >
                <Settings className="w-4 h-4" />
                <span>AI Settings</span>
                {hasChanges && <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" style={{
                    boxShadow: '0 0 6px rgba(239,68,68,0.4)',
                }}></span>}
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    <div className="ai-settings-overlay fixed inset-0 z-40 bg-slate-900/45 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

                    <div className="ai-settings-modal fixed inset-3 md:inset-8 z-50 rounded-3xl p-5 md:p-7 flex flex-col overflow-hidden" style={neuFloat}>
                        <div className="flex items-center justify-between gap-4 mb-5 shrink-0 border-b border-slate-200/70 pb-4">
                            <div>
                                <h3 className="text-xl md:text-2xl font-bold text-slate-800">AI Settings</h3>
                                <p className="text-xs text-slate-400 mt-1">Configure pipeline, Agentic AI, extraction engines, and models.</p>
                            </div>
                            <button
                                onClick={handleReset}
                                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 px-2.5 py-1.5 rounded-xl transition-all duration-150"
                                title="Reset to defaults"
                                style={neuRaised}
                            >
                                <RefreshCw className="w-3 h-3" />
                                Reset
                            </button>
                        </div>

                        <div className="grid grid-cols-3 gap-2 shrink-0 mb-5 rounded-2xl p-1.5" style={neuInset}>
                            {[
                                { id: 'pipeline', label: 'Pipeline Steps', Icon: Power },
                                { id: 'agent', label: 'Agentic AI', Icon: Cpu },
                                { id: 'models', label: 'Models & Extraction', Icon: Sparkles },
                            ].map(tab => {
                                const active = activeSettingsTab === tab.id;
                                const TabIcon = tab.Icon;
                                return (
                                    <button key={tab.id} onClick={() => setActiveSettingsTab(tab.id)}
                                        className={`min-w-0 px-2 md:px-4 py-2.5 rounded-xl text-[10px] sm:text-xs md:text-sm font-bold transition-all flex items-center justify-center gap-1.5 md:gap-2 ${active ? 'text-white' : 'text-slate-500 hover:text-slate-700'}`}
                                        style={active ? {
                                            background: '#0f6cbd',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                        } : {}}>
                                        <TabIcon className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
                                        <span className="truncate">{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto pr-1 md:pr-3 grid grid-cols-1 xl:grid-cols-2 gap-x-8 content-start">

                        {/* ══ DESCRIPTION SEARCH TOGGLE ══════════════════════════ */}
                        <div className={activeSettingsTab === 'pipeline' ? 'mb-5 xl:col-span-2' : 'hidden'}>
                            <button
                                onClick={() => { setSearchWithDescription(prev => !prev); setHasChanges(true); }}
                                className="w-full px-4 py-3 rounded-xl text-left transition-all duration-200 flex items-center gap-3"
                                style={{
                                    background: 'var(--neu-bg)',
                                    boxShadow: searchWithDescription
                                        ? 'inset 2px 2px 5px rgba(163,177,198,0.35), inset -2px -2px 5px rgba(255,255,255,0.75), 0 0 0 2px rgba(52,211,153,0.30), 0 0 16px rgba(52,211,153,0.32)'
                                        : '2px 2px 6px 0 rgba(163,177,198,0.35), -2px -2px 6px 0 rgba(255,255,255,0.5), 0 0 0 2px rgba(251,191,36,0.35)',
                                }}
                            >
                                <div className={`relative w-9 h-5 rounded-full transition-all duration-300 shrink-0 ${searchWithDescription ? 'bg-emerald-400' : 'bg-slate-300'}`}>
                                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300 ${searchWithDescription ? 'left-[18px]' : 'left-0.5'}`}
                                        style={{ boxShadow: '1px 1px 3px rgba(0,0,0,0.15)' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <FileText className="w-3.5 h-3.5 text-slate-600" />
                                        <span className="text-xs font-semibold text-slate-800">Description in Serper search</span>
                                    </div>
                                    <p className="text-[10px] mt-0.5 ml-6 text-slate-500">
                                        {searchWithDescription
                                            ? 'ON — Serper searches the Part Number and the product Description, then scraping and mapping as usual.'
                                            : 'OFF — Serper searches the Part Number only. AI still picks the Manufacturer and Vendor websites; the rest of the flow is unchanged.'}
                                    </p>
                                </div>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0 ${searchWithDescription ? 'text-emerald-700' : 'text-amber-700'}`}
                                    style={{ background: 'var(--neu-bg)', boxShadow: 'inset 1px 1px 2px rgba(163,177,198,0.3), inset -1px -1px 2px rgba(255,255,255,0.5)' }}>
                                    {searchWithDescription ? 'ON' : 'OFF'}
                                </span>
                            </button>
                        </div>

                        {/* ══ PIPELINE STEP TOGGLES ══════════════════════════════ */}
                        <div className={activeSettingsTab === 'pipeline' ? 'mb-5 xl:col-span-2' : 'hidden'}>
                            <label className="flex items-center gap-1.5 text-sm font-bold text-slate-600 mb-3">
                                <Power className="w-4 h-4 text-emerald-500" />
                                Pipeline Steps
                                <span className="text-xs font-normal text-slate-400">
                                    ({Object.values(pipelineSteps).filter(Boolean).length}/{PIPELINE_STEPS.length} enabled)
                                </span>
                            </label>
                            <div className="space-y-1.5">
                                {PIPELINE_STEPS.map(step => {
                                    const isOn = pipelineSteps[step.id] !== false;
                                    const StepIcon = pipelineIcons[step.id] || Settings;
                                    return (
                                        <button
                                            key={step.id}
                                            onClick={() => {
                                                setPipelineSteps(prev => ({ ...prev, [step.id]: !isOn }));
                                                setHasChanges(true);
                                            }}
                                            className="w-full px-3.5 py-2.5 rounded-xl text-left transition-all duration-200 flex items-center gap-3"
                                            style={{
                                                background: 'var(--neu-bg)',
                                                boxShadow: isOn
                                                    ? 'inset 2px 2px 5px rgba(163,177,198,0.35), inset -2px -2px 5px rgba(255,255,255,0.75), 0 0 0 2px rgba(52,211,153,0.30), 0 0 16px rgba(52,211,153,0.32)'
                                                    : '2px 2px 6px 0 rgba(163,177,198,0.35), -2px -2px 6px 0 rgba(255,255,255,0.5)',
                                            }}
                                        >
                                            {/* Toggle switch */}
                                            <div className={`relative w-9 h-5 rounded-full transition-all duration-300 shrink-0 ${
                                                isOn ? 'bg-emerald-400' : 'bg-slate-300'
                                            }`} style={isOn ? { boxShadow: '0 0 8px rgba(52,211,153,0.4)' } : {}}>
                                                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300 ${
                                                    isOn ? 'left-[18px]' : 'left-0.5'
                                                }`} style={{
                                                    boxShadow: '1px 1px 3px rgba(0,0,0,0.15)',
                                                }} />
                                            </div>
                                            {/* Label */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <StepIcon className={`w-3.5 h-3.5 ${isOn ? 'text-slate-600' : 'text-slate-300'}`} />
                                                    <span className={`text-xs font-semibold ${isOn ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                                                        Step {step.step}: {step.label}
                                                    </span>
                                                </div>
                                                <p className={`text-[10px] mt-0.5 ml-6 ${isOn ? 'text-slate-400' : 'text-slate-300'}`}>
                                                    {step.desc}
                                                </p>
                                            </div>
                                            {/* Status badge */}
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0 ${
                                                isOn ? 'text-emerald-700' : 'text-slate-400'
                                            }`} style={{
                                                background: 'var(--neu-bg)',
                                                boxShadow: 'inset 1px 1px 2px rgba(163,177,198,0.3), inset -1px -1px 2px rgba(255,255,255,0.5)',
                                            }}>{isOn ? 'ON' : 'OFF'}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Divider */}
                        <div className={activeSettingsTab === 'pipeline' ? 'h-px rounded-full mb-5 xl:col-span-2' : 'hidden'} style={{
                            background: 'linear-gradient(90deg, transparent, rgba(163,177,198,0.3), transparent)',
                        }} />

                        {/* Agentic URL Recovery */}
                        <div className={activeSettingsTab === 'agent' ? 'mb-5 xl:col-span-2' : 'hidden'}>
                            <label className="flex items-center gap-1.5 text-sm font-bold text-slate-600 mb-3">
                                <Sparkles className="w-4 h-4 text-violet-500" />
                                Agentic URL Recovery
                                <span className="text-xs font-normal text-slate-400">(no-result fallback)</span>
                            </label>
                            <button
                                onClick={() => { setAgent(prev => ({ ...prev, enabled: !prev.enabled })); setHasChanges(true); }}
                                className="w-full px-4 py-3 rounded-2xl flex items-center justify-between"
                                style={agent.enabled ? {
                                    background: 'linear-gradient(135deg, rgba(236,253,245,0.95), rgba(240,249,255,0.95))',
                                    boxShadow: '0 0 0 2px rgba(16,185,129,0.32), 0 0 20px rgba(16,185,129,0.28), 4px 4px 10px rgba(163,177,198,0.35)',
                                } : neuRaised}
                            >
                                <div className="text-left">
                                    <p className="text-sm font-bold text-slate-700">Enable Agentic AI</p>
                                    <p className="text-[10px] text-slate-400">Gemini plans alternate queries and Serper searches them.</p>
                                </div>
                                <span className={`px-2 py-1 rounded-lg text-xs font-bold ${agent.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                                    {agent.enabled ? 'ON' : 'OFF'}
                                </span>
                            </button>
                            <fieldset disabled={!agent.enabled}
                                className={`mt-3 space-y-3 rounded-2xl p-4 transition-opacity ${agent.enabled ? 'opacity-100' : 'opacity-55'}`}
                                style={neuInset}>
                                    <ModelPicker
                                        icon={Cpu}
                                        title="Planner model"
                                        description="Plans the alternate search queries."
                                        options={AVAILABLE_MODELS}
                                        value={agent.plannerModel}
                                        onSelect={id => { setAgent(prev => ({ ...prev, plannerModel: id })); setHasChanges(true); }}
                                    />
                                    <button type="button"
                                        onClick={() => { setAgent(prev => ({ ...prev, triggerOnNoResults: !prev.triggerOnNoResults })); setHasChanges(true); }}
                                        className={`w-full flex items-center justify-between rounded-xl border-2 p-3 text-xs font-semibold transition ${agent.triggerOnNoResults ? 'border-emerald-300 bg-emerald-50 text-emerald-800 shadow-[0_0_14px_rgba(16,185,129,0.18)]' : 'border-slate-200 bg-white/70 text-slate-600'}`}>
                                        <span className="text-left">Run when no URLs are found</span>
                                        <span className={`relative w-10 h-6 rounded-full transition-colors ${agent.triggerOnNoResults ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                                            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${agent.triggerOnNoResults ? 'left-5' : 'left-1'}`} />
                                        </span>
                                    </button>
                                    {[
                                        ['maxSearchRounds', 'Maximum search rounds', 1, 5],
                                        ['maxQueriesPerRound', 'Queries per round', 1, 8],
                                        ['resultsPerQuery', 'Results per query', 1, 10],
                                        ['maxCandidateUrls', 'Maximum candidate URLs', 5, 30],
                                        ['timeoutSeconds', 'Timeout (seconds)', 30, 300],
                                    ].map(([key, label, min, max]) => (
                                        <label key={key} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-white/70 p-3 text-xs font-semibold text-slate-600 focus-within:border-cyan-400 focus-within:ring-4 focus-within:ring-cyan-100/80 transition">
                                            <span>{label}</span>
                                            <input type="number" min={min} max={max} value={agent[key]}
                                                onChange={e => {
                                                    const value = Math.max(min, Math.min(max, Number(e.target.value) || min));
                                                    setAgent(prev => ({ ...prev, [key]: value }));
                                                    setHasChanges(true);
                                                }}
                                                className="w-24 rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-right text-sm font-bold text-slate-700 outline-none focus:border-cyan-500" />
                                        </label>
                                    ))}
                            </fieldset>
                        </div>

                        <div className={activeSettingsTab === 'agent' ? 'h-px rounded-full mb-5 xl:col-span-2' : 'hidden'} style={{
                            background: 'linear-gradient(90deg, transparent, rgba(163,177,198,0.3), transparent)',
                        }} />

                        {/* ══ AI MODEL PER STEP ══════════════════════════════════════ */}
                        <div className={activeSettingsTab === 'models' ? 'xl:col-span-2 rounded-2xl border border-blue-200 bg-blue-50/80 p-4 mb-1' : 'hidden'}>
                            <div className="flex items-start gap-3">
                                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-bold text-slate-800">Choose which AI handles each step</p>
                                    <p className="text-xs leading-5 text-slate-600 mt-1">Every step offers the same models. Changing one step does not change the others. The current choice has a blue border and a Selected label.</p>
                                </div>
                            </div>
                        </div>

                        {activeSettingsTab === 'models' && (
                            <>
                                <ModelPicker
                                    number="2"
                                    icon={ListFilter}
                                    title="Step 2 — AI Link Ranking"
                                    description="Picks the manufacturer and vendor links with confidence and part number match."
                                    options={AVAILABLE_MODELS}
                                    value={classificationModel}
                                    onSelect={id => { setClassificationModel(id); setHasChanges(true); }}
                                />
                                <ModelPicker
                                    number="4"
                                    icon={Cpu}
                                    title="Step 4 — Spec Extraction"
                                    description="Cleans and structures specifications from the scraped web page."
                                    options={AVAILABLE_MODELS}
                                    value={specExtractModel}
                                    onSelect={id => { setSpecExtractModel(id); setHasChanges(true); }}
                                />
                                <ModelPicker
                                    number="6"
                                    icon={Sparkles}
                                    title="Step 6 — AI Extraction (final mapping)"
                                    description="Maps the prepared product data into the final INC properties, with few-shot memory."
                                    options={STEP6_MODEL_OPTIONS}
                                    value={step6Model}
                                    onSelect={selectStep6Model}
                                />
                                <ModelPicker
                                    number="6"
                                    icon={Database}
                                    title="Step 6 — Missing Property Conditions"
                                    description="Called only when the database has no saved rule for a property. The rule is saved and reused."
                                    options={AVAILABLE_MODELS}
                                    value={conditionGenerationModel}
                                    onSelect={id => { setConditionGenerationModel(id); setHasChanges(true); }}
                                />
                            </>
                        )}

                        {/* ── Active Config Summary ────────────────── */}
                        <div className={activeSettingsTab === 'models' ? 'rounded-2xl border border-blue-200 bg-blue-50/70 p-5 mb-5 xl:col-span-2' : 'hidden'}>
                            <div className="flex items-center gap-2 mb-3">
                                <ActiveEngineIcon className="w-5 h-5 text-blue-600" />
                                <p className="text-sm font-bold text-slate-800">Current AI configuration</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                {[
                                    ['Step 2 — Link ranking', classificationModel],
                                    ['Step 4 — Spec extraction', specExtractModel],
                                    ['Step 6 — Final mapping', step6Model],
                                    ['Step 6 — Missing conditions', conditionGenerationModel],
                                    ['Agentic URL recovery planner', agent.plannerModel],
                                ].map(([label, id]) => (
                                    <div key={label} className="rounded-lg bg-white border border-blue-100 p-3">
                                        <span className="block text-slate-400 mb-1">{label}</span>
                                        <strong className="text-slate-700">{modelLabel(id)}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── Actions ──────────────────────────────── */}
                        </div>

                        <div className="flex justify-end gap-3 shrink-0 border-t border-slate-200/70 pt-4 mt-4">
                            <button onClick={() => setIsOpen(false)} className="btn-secondary flex-1">
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    handleSave();
                                    setIsOpen(false);
                                }}
                                disabled={!hasChanges}
                                className="flex-1 py-2.5 rounded-2xl font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                style={{
                                    background: '#0f6cbd',
                                    boxShadow: hasChanges
                                        ? '0 2px 8px rgba(0,0,0,0.14)'
                                        : '3px 3px 8px 0 rgba(163,177,198,0.4), -3px -3px 8px 0 rgba(255,255,255,0.6)',
                                }}
                            >
                                <Save className="w-4 h-4" />
                                Save Settings
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default SettingsDropdown;
